import { useState, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { usePreferences } from '../contexts/PreferencesContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { Switch } from '../components/ui/switch';
import { Input } from '../components/ui/input';
import { Settings, Palette, Type, Layout, Image, RotateCcw, User, Mail, Calendar, Sparkles, Bell, Clock, Globe, Gift, Check, Loader2, ShieldCheck, HardDrive, Download, Trash2, Eye, FileText, Mic, Brain, BarChart3 } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import AmbassadorStatusCard from '../components/AmbassadorStatusCard';
import { LANGUAGES } from '../i18n';
import ReferralSection from '../components/settings/ReferralSection';
import NotificationSettings from '../components/settings/NotificationSettings';
import StorageDashboard from '../components/settings/StorageDashboard';
import LMSSection from '../components/settings/LMSSection';
import NotificationPreferencesCard from '../components/NotificationPreferencesCard';
import GradeTrackerCard from '../components/GradeTrackerCard';
import RetentionPillarsCard from '../components/RetentionPillarsCard';
import TermsCard from '../components/TermsCard';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SettingsPage = () => {
  const { user, logout } = useAuth();
  const { preferences, updatePreference, uploadBackground, removeBackground, resetToDefaults, loading } = usePreferences();
  const { t, i18n } = useTranslation();
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Privacy state
  const [consent, setConsent] = useState(null);
  const [consentLoading, setConsentLoading] = useState(true);
  const [processingLog, setProcessingLog] = useState([]);
  const [logLoading, setLogLoading] = useState(false);
  const [showLog, setShowLog] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Load consent on mount
  useState(() => {
    const loadConsent = async () => {
      try {
        const res = await axios.get(`${API}/privacy/consent`);
        setConsent(res.data);
      } catch (e) {
        console.error('Failed to load consent:', e);
      } finally {
        setConsentLoading(false);
      }
    };
    loadConsent();
  }, []);

  const updateConsent = async (field, value) => {
    try {
      await axios.put(`${API}/privacy/consent`, { [field]: value });
      setConsent(prev => ({ ...prev, [field]: value }));
      toast.success(`${field.replace(/_/g, ' ')} consent ${value ? 'granted' : 'revoked'}`);
    } catch (e) {
      toast.error('Failed to update consent');
    }
  };

  const loadProcessingLog = async () => {
    setLogLoading(true);
    try {
      const res = await axios.get(`${API}/privacy/processing-log?limit=50`);
      setProcessingLog(res.data.logs || []);
    } catch (e) {
      toast.error('Failed to load processing log');
    } finally {
      setLogLoading(false);
    }
  };

  const handleExportData = async () => {
    setExportLoading(true);
    try {
      const res = await axios.get(`${API}/privacy/export`);
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `istudypro-data-export-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Data exported successfully');
    } catch (e) {
      toast.error('Failed to export data');
    } finally {
      setExportLoading(false);
    }
  };

  const handleHardDelete = async () => {
    if (!window.confirm('Are you absolutely sure? This will permanently delete ALL your data including lectures, flashcards, chat history, files, and your account. This is IRREVERSIBLE.')) return;
    if (!window.confirm('FINAL WARNING: All data will be permanently erased from our servers. Type "DELETE" in the next prompt to confirm.')) return;
    const confirmation = window.prompt('Type DELETE to confirm permanent data deletion:');
    if (confirmation !== 'DELETE') {
      toast.info('Deletion cancelled');
      return;
    }
    setDeleteLoading(true);
    try {
      await axios.delete(`${API}/privacy/hard-delete`);
      toast.success('All data permanently deleted');
      setTimeout(() => logout(), 2000);
    } catch (e) {
      toast.error('Failed to delete data');
    } finally {
      setDeleteLoading(false);
    }
  };

  const changeLanguage = (langCode) => {
    i18n.changeLanguage(langCode);
    toast.success(`Language changed to ${LANGUAGES.find(l => l.code === langCode)?.name}`);
  };

  const themes = [
    { value: 'light', label: t('settings.light'), icon: '☀️', color: 'bg-white' },
    { value: 'dark', label: t('settings.dark'), icon: '🌙', color: 'bg-gray-900' },
    { value: 'high-contrast', label: 'High Contrast', icon: '🔆', color: 'bg-black' }
  ];

  const layouts = [
    { value: 'grid', label: 'Grid View', icon: '🟦' },
    { value: 'list', label: 'List View', icon: '📝' }
  ];

  const typographies = [
    { value: 'small', label: 'Small', size: 14 },
    { value: 'medium', label: 'Medium', size: 16 },
    { value: 'large', label: 'Large', size: 18 }
  ];

  const accentColors = [
    '#1A4D2E', '#10B981', '#3B82F6', '#8B5CF6', 
    '#EC4899', '#F59E0B', '#EF4444', '#06B6D4'
  ];

  const handleBackgroundUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    setUploading(true);
    try {
      await uploadBackground(file);
    } catch (error) {
      console.error('Upload error:', error);
    } finally {
      setUploading(false);
    }
  };

  const handleReset = async () => {
    if (window.confirm('Reset all settings to default? This cannot be undone.')) {
      await resetToDefaults();
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96">Loading preferences...</div>;
  }

  return (
    <div className="f500-page max-w-5xl mx-auto px-4 py-8 lg:py-12 space-y-8" data-testid="settings-page">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-end gap-4 border-b border-[var(--f500-line)] pb-8">
        <div>
          <p className="f500-eyebrow mb-3">— Settings</p>
          <h1 className="font-serif-accent f500-display text-4xl sm:text-5xl">Tune the experience.</h1>
          <p className="f500-body text-base mt-3 max-w-xl">
            Theme, language, notifications, privacy. Configure iStudyPro to match how you work.
          </p>
        </div>
        <button
          onClick={handleReset}
          className="f500-btn f500-btn-secondary"
          data-testid="reset-button"
        >
          <RotateCcw className="w-4 h-4" />
          Reset to Default
        </button>
      </div>

      {/* User Profile Card */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className="f500-card p-6 lg:p-8">
          <div className="flex items-center gap-5">
            <div className="w-20 h-20 bg-[var(--f500-ink)] flex items-center justify-center text-white text-2xl f500-h1">
              {user?.name?.charAt(0).toUpperCase() || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="f500-h2 text-2xl flex items-center gap-2 truncate">
                {user?.name}
                <Sparkles className="w-4 h-4 text-[var(--f500-blue)]" strokeWidth={1.75} />
              </h2>
              <div className="flex items-center gap-2 text-[var(--f500-ink-2)] mt-1.5 text-sm" style={{ fontFamily: 'var(--sf-text)' }}>
                <Mail className="w-3.5 h-3.5" strokeWidth={1.75} />
                <span className="truncate">{user?.email}</span>
              </div>
              <div className="flex items-center gap-2 text-[var(--f500-ink-3)] mt-1 text-xs" style={{ fontFamily: 'var(--sf-text)' }}>
                <Calendar className="w-3 h-3" strokeWidth={1.75} />
                <span>Member since {user?.created_at ? format(new Date(user.created_at), 'PPP') : 'recently'}</span>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Theme Selection */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
      >
        <Card className="f500-card p-6 lg:p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <Palette className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h3 className="text-xl font-bold">Theme</h3>
              <p className="text-sm text-muted-foreground">Choose your visual style</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {themes.map((theme) => (
              <motion.button
                key={theme.value}
                onClick={() => updatePreference('theme', theme.value)}
                className={`p-6 rounded-xl border-2 transition-all ${
                  preferences.theme === theme.value
                    ? 'border-primary bg-primary/5 scale-105'
                    : 'border-border hover:border-primary/50'
                }`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                data-testid={`theme-${theme.value}`}
              >
                <div className="text-4xl mb-2">{theme.icon}</div>
                <div className="font-medium">{theme.label}</div>
                {preferences.theme === theme.value && (
                  <div className="mt-2 text-xs text-primary font-medium">✓ Active</div>
                )}
              </motion.button>
            ))}
          </div>
        </Card>
      </motion.div>

      {/* Language Selection */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.15 }}
      >
        <Card className="f500-card p-6 lg:p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-green-500/10 flex items-center justify-center">
              <Globe className="w-6 h-6 text-green-500" />
            </div>
            <div>
              <h3 className="text-xl font-bold">{t('settings.language')}</h3>
              <p className="text-sm text-muted-foreground">Choose your preferred language</p>
            </div>
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
            {LANGUAGES.map((lang) => (
              <motion.button
                key={lang.code}
                onClick={() => changeLanguage(lang.code)}
                className={`p-4 rounded-xl border-2 transition-all ${
                  i18n.language === lang.code
                    ? 'border-green-500 bg-green-500/5 scale-105'
                    : 'border-border hover:border-green-500/50'
                }`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                data-testid={`lang-${lang.code}`}
              >
                <div className="text-2xl mb-1">{lang.flag}</div>
                <div className="text-sm font-medium">{lang.name}</div>
                {i18n.language === lang.code && (
                  <div className="mt-1 text-xs text-green-500 font-medium">✓</div>
                )}
              </motion.button>
            ))}
          </div>
        </Card>
      </motion.div>

      {/* Layout Selection */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.2 }}
      >
        <Card className="f500-card p-6 lg:p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center">
              <Layout className="w-6 h-6 text-blue-500" />
            </div>
            <div>
              <h3 className="text-xl font-bold">Layout</h3>
              <p className="text-sm text-muted-foreground">How content is displayed</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {layouts.map((layout) => (
              <motion.button
                key={layout.value}
                onClick={() => updatePreference('layout', layout.value)}
                className={`p-6 rounded-xl border-2 transition-all ${
                  preferences.layout === layout.value
                    ? 'border-blue-500 bg-blue-500/5 scale-105'
                    : 'border-border hover:border-blue-500/50'
                }`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                data-testid={`layout-${layout.value}`}
              >
                <div className="text-4xl mb-2">{layout.icon}</div>
                <div className="font-medium">{layout.label}</div>
                {preferences.layout === layout.value && (
                  <div className="mt-2 text-xs text-blue-500 font-medium">✓ Active</div>
                )}
              </motion.button>
            ))}
          </div>
        </Card>
      </motion.div>

      {/* Typography */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.3 }}
      >
        <Card className="f500-card p-6 lg:p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center">
              <Type className="w-6 h-6 text-purple-500" />
            </div>
            <div>
              <h3 className="text-xl font-bold">Typography</h3>
              <p className="text-sm text-muted-foreground">Adjust text size for comfort</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            {typographies.map((typo) => (
              <motion.button
                key={typo.value}
                onClick={() => updatePreference('typography', typo.value)}
                className={`p-6 rounded-xl border-2 transition-all ${
                  preferences.typography === typo.value
                    ? 'border-purple-500 bg-purple-500/5 scale-105'
                    : 'border-border hover:border-purple-500/50'
                }`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                data-testid={`typography-${typo.value}`}
              >
                <div className="font-medium" style={{ fontSize: typo.size }}>{typo.label}</div>
                <div className="text-xs text-muted-foreground mt-1">{typo.size}px</div>
                {preferences.typography === typo.value && (
                  <div className="mt-2 text-xs text-purple-500 font-medium">✓ Active</div>
                )}
              </motion.button>
            ))}
          </div>
          
          <div className="p-4 rounded-xl bg-muted/50">
            <p style={{ fontSize: (typographies.find(t => t.value === preferences.typography)?.size || 16) + 'px' }}>
              This is how your text will look with the selected size. Make sure it's comfortable to read! 📚
            </p>
          </div>
        </Card>
      </motion.div>

      {/* Accent Color */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.4 }}
      >
        <Card className="f500-card p-6 lg:p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-pink-500/10 flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-pink-500" />
            </div>
            <div>
              <h3 className="text-xl font-bold">Accent Color</h3>
              <p className="text-sm text-muted-foreground">Your brand color for buttons & links</p>
            </div>
          </div>
          
          <div className="grid grid-cols-4 sm:grid-cols-8 gap-3">
            {accentColors.map((color) => (
              <motion.button
                key={color}
                onClick={() => updatePreference('accent_color', color)}
                className={`w-full aspect-square rounded-xl border-4 transition-all ${
                  preferences.accent_color === color
                    ? 'border-foreground scale-110 shadow-lg'
                    : 'border-transparent hover:scale-105'
                }`}
                style={{ backgroundColor: color }}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                data-testid={`color-${color}`}
              />
            ))}
          </div>
          
          <div className="mt-6 p-4 rounded-xl" style={{ backgroundColor: preferences.accent_color + '20' }}>
            <Button 
              className="rounded-full"
              style={{ backgroundColor: preferences.accent_color }}
            >
              Preview Button Color
            </Button>
          </div>
        </Card>
      </motion.div>

      {/* Background Image */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.5 }}
      >
        <Card className="f500-card p-6 lg:p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-orange-500/10 flex items-center justify-center">
              <Image className="w-6 h-6 text-orange-500" />
            </div>
            <div>
              <h3 className="text-xl font-bold">Background Image</h3>
              <p className="text-sm text-muted-foreground">Personalize with your own image</p>
            </div>
          </div>
          
          {preferences.background_image ? (
            <div className="space-y-4">
              <div 
                className="w-full h-48 rounded-xl bg-cover bg-center border-2 border-border"
                style={{ backgroundImage: `url(${process.env.REACT_APP_BACKEND_URL}${preferences.background_image})` }}
              />
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="flex-1 rounded-full"
                >
                  Change Image
                </Button>
                <Button
                  variant="destructive"
                  onClick={removeBackground}
                  className="flex-1 rounded-full"
                  data-testid="remove-background"
                >
                  Remove
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-center py-12 border-2 border-dashed border-border rounded-xl hover:border-primary transition-colors">
              <Image className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground mb-4">No background image set</p>
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="rounded-full"
                data-testid="upload-background"
              >
                {uploading ? 'Uploading...' : 'Upload Image'}
              </Button>
            </div>
          )}
          
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={handleBackgroundUpload}
            className="hidden"
          />
          
          <p className="text-xs text-muted-foreground mt-4">
            💡 Tip: Use a subtle image for best readability. Max size: 5MB
          </p>
        </Card>

      {/* Ambassador status */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.55 }}
      >
        <AmbassadorStatusCard variant="full" />
      </motion.div>

      </motion.div>
      
      {/* Notification Settings */}
      <NotificationSettings />
      
      {/* Referral Section */}
      <ReferralSection />

      {/* Personal Storage Dashboard */}
      <StorageDashboard />

      {/* University LMS Integration */}
      <LMSSection />

      {/* Privacy & Data (GDPR) */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.6 }}
      >
        <Card className="f500-card p-6 lg:p-8" data-testid="privacy-section">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-emerald-500" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Transparency Center</h2>
              <p className="text-sm text-muted-foreground">Ghana DPA 2012 & GDPR compliant privacy controls</p>
            </div>
          </div>

          {/* Consent Toggles */}
          <div className="space-y-4 mb-6">
            <h3 className="font-semibold text-sm flex items-center gap-2">
              <Check className="w-4 h-4 text-emerald-500" />
              Data Consent Preferences
            </h3>
            <p className="text-xs text-muted-foreground">Control how your data is processed. You can revoke consent at any time.</p>
            
            {consentLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading consent preferences...
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="p-3 rounded-xl bg-muted/30 flex items-center justify-between" data-testid="consent-audio">
                  <div className="flex items-center gap-2">
                    <Mic className="w-4 h-4 text-pink-500" />
                    <div>
                      <p className="text-sm font-medium">Audio Recording</p>
                      <p className="text-[10px] text-muted-foreground">Allow cloud transcription of recordings</p>
                    </div>
                  </div>
                  <Switch
                    checked={consent?.audio_recording || false}
                    onCheckedChange={(v) => updateConsent('audio_recording', v)}
                    data-testid="consent-audio-toggle"
                  />
                </div>
                <div className="p-3 rounded-xl bg-muted/30 flex items-center justify-between" data-testid="consent-ai">
                  <div className="flex items-center gap-2">
                    <Brain className="w-4 h-4 text-purple-500" />
                    <div>
                      <p className="text-sm font-medium">AI Summarization</p>
                      <p className="text-[10px] text-muted-foreground">Allow AI to analyze your content</p>
                    </div>
                  </div>
                  <Switch
                    checked={consent?.ai_summarization || false}
                    onCheckedChange={(v) => updateConsent('ai_summarization', v)}
                    data-testid="consent-ai-toggle"
                  />
                </div>
                <div className="p-3 rounded-xl bg-muted/30 flex items-center justify-between" data-testid="consent-analytics">
                  <div className="flex items-center gap-2">
                    <BarChart3 className="w-4 h-4 text-cyan-500" />
                    <div>
                      <p className="text-sm font-medium">Usage Analytics</p>
                      <p className="text-[10px] text-muted-foreground">Help improve iStudyPRO with anonymized data</p>
                    </div>
                  </div>
                  <Switch
                    checked={consent?.data_analytics || false}
                    onCheckedChange={(v) => updateConsent('data_analytics', v)}
                    data-testid="consent-analytics-toggle"
                  />
                </div>
                <div className="p-3 rounded-xl bg-muted/30 flex items-center justify-between" data-testid="consent-marketing">
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-amber-500" />
                    <div>
                      <p className="text-sm font-medium">Marketing Emails</p>
                      <p className="text-[10px] text-muted-foreground">Receive product updates and tips</p>
                    </div>
                  </div>
                  <Switch
                    checked={consent?.marketing_emails || false}
                    onCheckedChange={(v) => updateConsent('marketing_emails', v)}
                    data-testid="consent-marketing-toggle"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Data Processing Log */}
          <div className="space-y-3 mb-6">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-sm flex items-center gap-2">
                <Eye className="w-4 h-4 text-blue-500" />
                Data Processing Log
              </h3>
              <Button
                size="sm"
                variant="outline"
                className="rounded-xl text-xs gap-1"
                onClick={() => { setShowLog(!showLog); if (!showLog) loadProcessingLog(); }}
                data-testid="view-processing-log-btn"
              >
                <Eye className="w-3 h-3" />
                {showLog ? 'Hide' : 'View'} Log
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">Every time your data is processed, it's logged here for full transparency.</p>
            
            {showLog && (
              <div className="max-h-60 overflow-y-auto rounded-xl border bg-muted/20 p-3 space-y-2" data-testid="processing-log-list">
                {logLoading ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground py-4 justify-center">
                    <Loader2 className="w-4 h-4 animate-spin" /> Loading log...
                  </div>
                ) : processingLog.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No processing events recorded yet.</p>
                ) : (
                  processingLog.map((log, i) => (
                    <div key={`item-${i}`} className="flex items-start gap-2 text-xs p-2 rounded-lg bg-background/50 border">
                      <FileText className="w-3 h-3 mt-0.5 shrink-0 text-muted-foreground" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{log.action?.replace(/_/g, ' ')}</p>
                        <p className="text-muted-foreground">{log.purpose}</p>
                        <div className="flex gap-3 mt-1 text-[10px] text-muted-foreground">
                          <span>{log.data_type}</span>
                          <span>{log.retention}</span>
                          {log.no_train_header && <span className="text-emerald-500">X-No-Train</span>}
                        </div>
                      </div>
                      <span className="text-[10px] text-muted-foreground shrink-0">
                        {log.timestamp ? new Date(log.timestamp).toLocaleDateString() : ''}
                      </span>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Data Portability & Deletion */}
          <div className="space-y-3">
            <h3 className="font-semibold text-sm flex items-center gap-2">
              <HardDrive className="w-4 h-4 text-indigo-500" />
              Your Data Rights
            </h3>

            <div className="p-4 rounded-xl bg-muted/30">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium flex items-center gap-2">
                    <Download className="w-4 h-4 text-blue-500" />
                    Download My Brain
                  </h4>
                  <p className="text-xs text-muted-foreground mt-1">Export all your data (lectures, flashcards, chats, exam results) in JSON format. GDPR Art. 20.</p>
                </div>
                <Button
                  onClick={handleExportData}
                  disabled={exportLoading}
                  variant="outline"
                  className="rounded-xl gap-2 shrink-0"
                  data-testid="download-brain-btn"
                >
                  {exportLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                  {exportLoading ? 'Exporting...' : 'Export All'}
                </Button>
              </div>
            </div>

            <div className="p-4 rounded-xl border border-red-500/20 bg-red-500/5">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-red-500 flex items-center gap-2">
                    <Trash2 className="w-4 h-4" />
                    Hard-Delete All My Data
                  </h4>
                  <p className="text-xs text-red-400/70 mt-1">Permanently erase everything — database, files, storage. GDPR Art. 17 Right to Erasure. This is irreversible.</p>
                </div>
                <Button
                  onClick={handleHardDelete}
                  disabled={deleteLoading}
                  variant="destructive"
                  className="rounded-xl gap-2 shrink-0"
                  data-testid="hard-delete-btn"
                >
                  {deleteLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                  {deleteLoading ? 'Deleting...' : 'Delete Everything'}
                </Button>
              </div>
            </div>
          </div>

          {/* Privacy Compliance Info */}
          <div className="mt-6 p-4 rounded-xl bg-emerald-500/5 border border-emerald-500/20">
            <div className="flex items-start gap-2">
              <ShieldCheck className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
              <div className="text-xs text-muted-foreground leading-relaxed">
                <p className="font-semibold text-emerald-600 mb-1">Privacy Commitment</p>
                <p>iStudyPro is built to meet <span className="font-medium">GDPR</span>, <span className="font-medium">FERPA</span>, and <span className="font-medium">SOC 2</span> standards.
                Audio in Privacy Mode is processed on-device and never uploaded. Cloud audio is encrypted and deleted immediately after transcription.
                We send <span className="font-medium">X-No-Train headers</span> to all AI providers — your data is never used to train models.</p>
              </div>
            </div>
          </div>
        </Card>
      </motion.div>

      {/* Notification Preferences */}
      <NotificationPreferencesCard />

      {/* Phase 3: Grade Tracker — drives Dashboard "weakest class" prompt + AI Tutor weighting */}
      <GradeTrackerCard />

      {/* 5-year retention pillars: Snooze, Parent Digest, Tutor Memory */}
      <RetentionPillarsCard />

      {/* Pillar #1: Academic Terms (semesters) — powers cross-semester transcript + finals-week mode */}
      <TermsCard />
    </div>
  );
};

export default SettingsPage;
